// Map AWS Amplify / Cognito errors to short, human-friendly strings.
// Used by every /auth/* page so the wording is consistent.
//
// We intentionally collapse "user not found" + "wrong password" into a
// single generic message to keep the `PreventUserExistenceErrors: ENABLED`
// invariant honoured client-side too.

import { isNetworkError } from '../lib/networkError.ts';

interface NamedError {
  readonly name?: string;
  readonly message?: string;
}

function asNamed(err: unknown): NamedError {
  if (err && typeof err === 'object') {
    return err;
  }
  return {};
}

/**
 * True when Amplify rejected an auth call because a user is already
 * signed in on this device (`signIn` throws this instead of starting a
 * second session). Callers use it to re-sync state and show an
 * "already signed in" view rather than a generic error.
 */
export function isUserAlreadyAuthenticated(err: unknown): boolean {
  return asNamed(err).name === 'UserAlreadyAuthenticatedException';
}

/**
 * True when an error reflects an **invalid session** rather than a bad
 * credential — a *token* operation (silent refresh, GetUser) that Cognito
 * rejected because the refresh token was revoked or has expired.
 *
 * The canonical case is `NotAuthorizedException: "Token is inactive"`,
 * seen after a global sign-out / token revocation when the browser keeps
 * replaying the now-dead refresh token on the next session restore. A
 * failed password sign-in also throws `NotAuthorizedException`, but with
 * an "Incorrect username or password" message and no token wording — so
 * we gate on the message to avoid treating a wrong password as a dead
 * session. Callers use this to purge the local session and route back to
 * sign-in instead of looping on the dead token.
 */
export function isSessionInvalid(err: unknown): boolean {
  const { name, message } = asNamed(err);
  if (name !== 'NotAuthorizedException') {
    return false;
  }
  return /token is inactive|refresh token|token has been revoked|token has expired|revoked/i.test(
    message ?? '',
  );
}

export function humanizeAuthError(err: unknown): string {
  // A dead network surfaces here as a thrown fetch TypeError, an explicit
  // offline signal, or a RecaptchaError (offline, the script/anchor frame can't
  // load). Catch it first so a connectivity drop reads as such rather than as
  // the misleading "bot check failed" message below. This is the one branch
  // that hides the underlying error behind a generic message, so log the raw
  // detail to the console for debugging — every other branch's returned message
  // already reflects the real cause, so logging there would just be noise (and
  // would trip the e2e console guard on expected failures like a wrong
  // password or a stalled reCAPTCHA).
  if (isNetworkError(err)) {
    console.error('[auth] network failure', err);
    return "We couldn't reach the server. Check your connection and try again.";
  }

  const { name, message } = asNamed(err);
  const code = name ?? '';

  // Generic credential / existence errors
  if (
    code === 'NotAuthorizedException' ||
    code === 'UserNotFoundException' ||
    /incorrect username or password/i.test(message ?? '')
  ) {
    return 'Incorrect email or password.';
  }

  // reCAPTCHA token couldn't be minted on the client (script blocked,
  // offline, ad-blocker, or a stalled/timed-out check — common on mobile
  // browsers with strict tracking protection). Distinct from a
  // server-side score rejection, which fails closed and surfaces as the
  // generic credential error above by design (we don't reveal which
  // factor failed). Name the automated/bot check explicitly so the user
  // understands why a correct password still didn't get them in, and
  // point at the usual cause (a blocker / privacy setting).
  if (code === 'RecaptchaError') {
    return "We couldn't complete the automated security check that screens for bots. A browser extension or strict privacy/tracking setting is usually the cause — disable it or try another browser, then sign in again.";
  }

  // Sign-up specifics
  if (code === 'UsernameExistsException') {
    return 'An account with that email already exists.';
  }
  if (code === 'InvalidPasswordException') {
    return 'Password must be at least 12 characters with upper, lower, digit, and symbol.';
  }
  if (code === 'InvalidParameterException') {
    return message ?? 'One of the fields is invalid.';
  }

  // Confirmation / OTP specifics
  if (code === 'CodeMismatchException') {
    return 'That code is incorrect. Try again.';
  }
  if (code === 'ExpiredCodeException') {
    return 'That code has expired. Request a new one.';
  }

  // Session state — a user is already signed in on this device. Pages
  // generally handle this by showing an "already signed in" view, but
  // map it here too so any unguarded path stays friendly.
  if (code === 'UserAlreadyAuthenticatedException') {
    return "You're already signed in.";
  }

  // Rate / lockout
  if (code === 'LimitExceededException' || code === 'TooManyRequestsException') {
    return 'Too many attempts. Wait a few minutes and try again.';
  }
  if (code === 'TooManyFailedAttemptsException') {
    return 'Too many failed attempts. Wait a few minutes and try again.';
  }

  // Sign-up disabled at the pool level — surfaces clearly while the
  // infra repo's selfSignUpEnabled flip is pending.
  if (/sign\s*up is not allowed/i.test(message ?? '')) {
    return 'Sign-up is not yet enabled on this pool. (Pending infra change in the sibling repo.)';
  }

  return message ?? 'Something went wrong. Please try again.';
}
